/**
 * Zugänge und Rollen (Spec 8.2, lokale Variante). Nur für die Administration.
 *
 * Das sind die Leute, die mit der App arbeiten — nicht die Mitarbeitenden, die
 * Trinkgeld bekommen. Jede Änderung steht im Audit-Log, mit dem, der sie
 * gemacht hat.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import type { Role } from "../../lib/auth";
import { dateTime } from "../../lib/format";
import { Empty, ErrorBox, Info, Section, Spinner } from "../../components/ui";

interface UserDto {
  id: string;
  username: string;
  displayName: string;
  email: string | null;
  microsoftLinked: boolean;
  passwordAccess: boolean;
  role: Role;
  roleLabel: string;
  active: boolean;
  mustChangePassword: boolean;
  totpEnabled: boolean;
  totpRequired: boolean;
  lockedUntil: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

const ROLLEN: { value: Role; label: string; text: string }[] = [
  {
    value: "Tips.Finance",
    label: "Buchhaltung",
    text: "Importieren, klären, rechnen, freigeben, auszahlen. Sieht IBANs.",
  },
  {
    value: "Tips.ShiftLead",
    label: "Schichtleitung",
    text: "Kassen klären und Stunden korrigieren. Keine Freigabe, keine IBANs.",
  },
  { value: "Tips.Viewer", label: "Lesend", text: "Nur ansehen. Keine Bankdaten." },
  {
    value: "Tips.Admin",
    label: "Administration",
    text: "Alles, dazu Stammdaten, Zugänge und das Wiedereröffnen von Tagen.",
  },
];

/** Das Einmalpasswort steht genau einmal auf dem Bildschirm. */
function OneTimePassword({
  user,
  password,
  microsoft,
  onClose,
}: {
  user: string;
  password: string;
  microsoft: boolean;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="card mb-6 border-accent p-5">
      <div className="label mb-2">· Einmalpasswort für {user}</div>
      <div className="mb-3 font-mono text-2xl tracking-wider">{password}</div>
      <p className="mb-4 text-sm text-muted">
        Jetzt weitergeben, am besten mündlich und getrennt vom Anmeldenamen. Es
        wird nicht noch einmal angezeigt und gilt nur für die erste Anmeldung; danach legt die
        Person ein eigenes Passwort fest.
        {microsoft
          ? " Wer sich mit Microsoft anmeldet, braucht es nicht — es bleibt der Ausweichweg."
          : ""}
      </p>
      <div className="flex gap-2">
        <button
          className="btn-outline"
          onClick={() => {
            void navigator.clipboard?.writeText(password).then(() => setCopied(true));
          }}
        >
          {copied ? "Kopiert" : "Kopieren"}
        </button>
        <button className="btn-ghost" onClick={onClose}>
          Weitergegeben, ausblenden
        </button>
      </div>
    </div>
  );
}

export function Users({ selfId }: { selfId: string }) {
  const queryClient = useQueryClient();
  const users = useQuery({
    queryKey: ["users"],
    queryFn: () =>
      api.get<{
        mailEnabled: boolean;
        microsoftEnabled: boolean;
        microsoftCertValidTo: string | null;
        microsoftRequests: LinkRequestDto[];
        users: UserDto[];
      }>("/users"),
  });
  const mailEnabled = users.data?.mailEnabled ?? false;
  const microsoftEnabled = users.data?.microsoftEnabled ?? false;
  const [emailEdit, setEmailEdit] = useState<{ id: string; value: string } | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [issued, setIssued] = useState<{ user: string; password: string } | null>(null);
  // Rollen werden vorgemerkt: ein Klick ins Auswahlfeld darf keinen Zugriff
  // auf Bankdaten geben oder nehmen.
  const [staged, setStaged] = useState<Record<string, Role>>({});
  // Folgenreiche Aktionen brauchen einen zweiten Klick.
  const [arming, setArming] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["users"] });

  const create = useMutation({
    mutationFn: (body: NewUserBody) => api.post<{ user: UserDto; oneTimePassword: string | null }>("/users", body),
    onSuccess: async (res) => {
      if (res.oneTimePassword) setIssued({ user: res.user.username, password: res.oneTimePassword });
      else setCreated(res.user.displayName);
      setAdding(false);
      await refresh();
    },
  });

  const passwordAccess = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.post<{ user: UserDto; oneTimePassword: string | null }>(`/users/${id}/password-access`, { enabled }),
    onSuccess: async (res) => {
      if (res.oneTimePassword) setIssued({ user: res.user.username, password: res.oneTimePassword });
      setArming(null);
      await refresh();
    },
  });

  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string; role?: Role; active?: boolean; email?: string | null }) =>
      api.patch<{ user: UserDto }>(`/users/${id}`, body),
    onSuccess: async (_res, vars) => {
      if (vars.email !== undefined) setEmailEdit(null);
      setStaged((prev) => {
        const next = { ...prev };
        delete next[vars.id];
        return next;
      });
      setArming(null);
      await refresh();
    },
  });

  const action = useMutation({
    mutationFn: ({
      id,
      what,
    }: {
      id: string;
      what: "password-reset" | "password-reset-link" | "totp-reset" | "unlock" | "microsoft-unlink";
    }) => api.post<{ user: UserDto; oneTimePassword?: string; sentTo?: string }>(`/users/${id}/${what}`),
    onSuccess: async (res) => {
      if (res.oneTimePassword) setIssued({ user: res.user.username, password: res.oneTimePassword });
      setSent(res.sentTo ? `Link an ${res.sentTo} geschickt, gültig 24 Stunden.` : null);
      setArming(null);
      await refresh();
    },
  });

  if (users.isLoading) return <Spinner />;
  if (users.error) return <ErrorBox error={users.error} />;
  const list = users.data?.users ?? [];

  const armed = (key: string) => arming === key;
  const twoStep = (key: string, label: string, run: () => void, danger = false) =>
    armed(key) ? (
      <span className="inline-flex gap-1">
        <button className={danger ? "btn-danger" : "btn-primary"} onClick={run}>
          Ja, {label.toLowerCase()}
        </button>
        <button className="btn-ghost" onClick={() => setArming(null)}>
          Nein
        </button>
      </span>
    ) : (
      <button className="btn-ghost" onClick={() => setArming(key)}>
        {label}
      </button>
    );

  return (
    <Section
      label="Zugänge"
      title="Zugänge"
      actions={
        adding ? null : (
          <button className="btn-primary" onClick={() => setAdding(true)}>
            Zugang anlegen
          </button>
        )
      }
    >
      {(() => {
        // Läuft das Zertifikat bald ab, geht „Mit Microsoft anmelden“ danach
        // nicht mehr — dann bleibt nur der Notfallzugang mit Passwort.
        const bis = users.data?.microsoftCertValidTo;
        if (!bis) return null;
        const tage = Math.floor((new Date(bis).getTime() - Date.now()) / 86_400_000);
        if (tage >= 45) return null;
        return (
          <div className="mb-6 rounded border border-warn/40 bg-warn/5 px-4 py-3 text-sm">
            Das Zertifikat für „Mit Microsoft anmelden“ läuft am {dateTime(bis).split(",")[0]} ab
            {tage > 0 ? `, in ${tage} Tagen` : ""}. Danach geht nur noch der Notfallzugang mit
            Passwort. Ein neues erzeugen und in Entra hochladen: docs/BETRIEB.md.
          </div>
        );
      })()}

      {created ? (
        <div className="card mb-6 flex items-center justify-between gap-4 border-ok/40 p-4 text-sm">
          <span>
            {created} ist angelegt, ohne Passwort. Meldet sich die Person zum ersten Mal mit
            Microsoft an, erscheint die Anmeldung hier oben unter „Wartet auf Zuordnung“ — dort
            bestätigen Sie sie einmal.
          </span>
          <button className="btn-ghost" onClick={() => setCreated(null)}>
            Ausblenden
          </button>
        </div>
      ) : null}

      {microsoftEnabled && (users.data?.microsoftRequests.length ?? 0) > 0 ? (
        <LinkRequests
          requests={users.data?.microsoftRequests ?? []}
          users={list.filter((u) => u.active && !u.microsoftLinked)}
          onDone={refresh}
        />
      ) : null}

      {sent ? (
        <div className="card mb-6 flex items-center justify-between gap-4 border-ok/40 p-4 text-sm">
          <span>{sent} Das bisherige Passwort gilt, bis der Link benutzt wird.</span>
          <button className="btn-ghost" onClick={() => setSent(null)}>
            Ausblenden
          </button>
        </div>
      ) : null}

      {issued ? (
        <OneTimePassword
          user={issued.user}
          password={issued.password}
          microsoft={microsoftEnabled}
          onClose={() => setIssued(null)}
        />
      ) : null}

      {adding ? (
        <NewUser
          pending={create.isPending}
          error={create.error}
          onCancel={() => {
            setAdding(false);
            create.reset();
          }}
          mailEnabled={mailEnabled}
          microsoftEnabled={microsoftEnabled}
          onSave={(body) => create.mutate(body)}
        />
      ) : null}

      {list.length === 0 ? (
        <Empty>Noch keine Zugänge.</Empty>
      ) : (
        <div className="card overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Rolle</th>
                <th>
                  Status
                  <Info text="Einmalpasswort offen: hat sich noch nicht angemeldet und eigenes Passwort gesetzt. Zweiter Faktor fehlt: wird bei der nächsten Anmeldung eingerichtet, bevor es weitergeht." />
                </th>
                <th>Zuletzt angemeldet</th>
                <th className="text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {list.map((u) => {
                const self = u.id === selfId;
                const role = staged[u.id] ?? u.role;
                const dirty = role !== u.role;
                return (
                  <tr key={u.id} className={u.active ? "" : "opacity-50"}>
                    <td>
                      <div>{u.displayName}</div>
                      <div className="font-mono text-xs text-muted">{u.username}</div>
                      {emailEdit?.id === u.id ? (
                        <form
                          className="mt-1 flex gap-1"
                          onSubmit={(e) => {
                            e.preventDefault();
                            update.mutate({ id: u.id, email: emailEdit.value.trim() || null });
                          }}
                        >
                          <input
                            className="field py-1 text-xs"
                            type="email"
                            autoFocus
                            placeholder="name@beispiel.de"
                            value={emailEdit.value}
                            onChange={(e) => setEmailEdit({ id: u.id, value: e.target.value })}
                          />
                          <button className="btn-primary" type="submit" disabled={update.isPending}>
                            OK
                          </button>
                          <button className="btn-ghost" type="button" onClick={() => setEmailEdit(null)}>
                            ×
                          </button>
                        </form>
                      ) : (
                        <button
                          className="text-xs text-muted underline-offset-2 hover:text-ink hover:underline"
                          onClick={() => setEmailEdit({ id: u.id, value: u.email ?? "" })}
                        >
                          {u.email ?? "E-Mail hinterlegen"}
                        </button>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <select
                          className="field w-40 py-1"
                          value={role}
                          disabled={!u.active}
                          onChange={(e) => setStaged((prev) => ({ ...prev, [u.id]: e.target.value as Role }))}
                        >
                          {ROLLEN.map((r) => (
                            <option key={r.value} value={r.value}>
                              {r.label}
                            </option>
                          ))}
                        </select>
                        {dirty ? (
                          <>
                            <button
                              className="btn-primary"
                              onClick={() => update.mutate({ id: u.id, role })}
                              disabled={update.isPending}
                            >
                              Übernehmen
                            </button>
                            <button
                              className="btn-ghost"
                              onClick={() =>
                                setStaged((prev) => {
                                  const next = { ...prev };
                                  delete next[u.id];
                                  return next;
                                })
                              }
                            >
                              Verwerfen
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {!u.active ? <span className="chip-muted">Deaktiviert</span> : null}
                        {u.lockedUntil ? (
                          <span className="chip-alert" title="Nach fünf Fehlversuchen für eine Viertelstunde">
                            Gesperrt bis {dateTime(u.lockedUntil).split(", ")[1]}
                          </span>
                        ) : null}
                        {u.microsoftLinked ? (
                          <span className="chip-ok" title="Meldet sich mit Microsoft an">
                            Microsoft
                          </span>
                        ) : null}
                        {/* Wer über Microsoft kommt, braucht sein Einmalpasswort nie. */}
                        {u.passwordAccess && u.mustChangePassword ? (
                          <span className="chip-warn">Einmalpasswort offen</span>
                        ) : null}
                        {microsoftEnabled && u.passwordAccess ? (
                          <span className="chip-muted" title="Kann sich zusätzlich mit Passwort anmelden">
                            Notfallzugang
                          </span>
                        ) : null}
                        {microsoftEnabled && !u.microsoftLinked && !u.passwordAccess ? (
                          <span className="chip-warn" title="Erste Anmeldung mit Microsoft steht noch aus">
                            Noch nicht verknüpft
                          </span>
                        ) : null}
                        {u.totpEnabled ? (
                          <span className="chip-ok">Zweiter Faktor</span>
                        ) : u.totpRequired ? (
                          <span className="chip-warn">Zweiter Faktor fehlt</span>
                        ) : null}
                        {self ? <span className="chip-muted">Sie</span> : null}
                      </div>
                    </td>
                    <td className="text-xs text-muted">{dateTime(u.lastLoginAt)}</td>
                    <td className="text-right">
                      <div className="flex flex-wrap justify-end gap-1">
                        {u.lockedUntil
                          ? twoStep(`unlock-${u.id}`, "Entsperren", () => action.mutate({ id: u.id, what: "unlock" }))
                          : null}
                        {u.active && u.passwordAccess && mailEnabled && u.email
                          ? twoStep(`link-${u.id}`, "Link per E-Mail", () =>
                              action.mutate({ id: u.id, what: "password-reset-link" }),
                            )
                          : null}
                        {u.active && u.passwordAccess
                          ? twoStep(
                              `pw-${u.id}`,
                              mailEnabled && u.email ? "Einmalpasswort" : "Passwort zurücksetzen",
                              () => action.mutate({ id: u.id, what: "password-reset" }),
                            )
                          : null}
                        {microsoftEnabled && u.active && !u.passwordAccess
                          ? twoStep(`pwon-${u.id}`, "Notfallzugang einrichten", () =>
                              passwordAccess.mutate({ id: u.id, enabled: true }),
                            )
                          : null}
                        {microsoftEnabled && u.passwordAccess
                          ? twoStep(
                              `pwoff-${u.id}`,
                              "Passwort entfernen",
                              () => passwordAccess.mutate({ id: u.id, enabled: false }),
                              true,
                            )
                          : null}
                        {u.microsoftLinked
                          ? twoStep(`ms-${u.id}`, "Microsoft lösen", () =>
                              action.mutate({ id: u.id, what: "microsoft-unlink" }),
                            )
                          : null}
                        {u.active && u.totpEnabled
                          ? twoStep(`totp-${u.id}`, "Zweiten Faktor zurücksetzen", () =>
                              action.mutate({ id: u.id, what: "totp-reset" }),
                            )
                          : null}
                        {self
                          ? null
                          : u.active
                            ? twoStep(
                                `off-${u.id}`,
                                "Deaktivieren",
                                () => update.mutate({ id: u.id, active: false }),
                                true,
                              )
                            : twoStep(`on-${u.id}`, "Aktivieren", () => update.mutate({ id: u.id, active: true }))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {update.error ? (
        <div className="mt-4">
          <ErrorBox error={update.error} />
        </div>
      ) : null}
      {passwordAccess.error ? (
        <div className="mt-4">
          <ErrorBox error={passwordAccess.error} />
        </div>
      ) : null}
      {action.error ? (
        <div className="mt-4">
          <ErrorBox error={action.error} />
        </div>
      ) : null}

      <p className="mt-3 text-xs text-muted">
        Deaktivieren statt löschen: das Audit-Log verweist weiter auf den Namen.{" "}
        {mailEnabled
          ? "Mit hinterlegter E-Mail-Adresse ist „Link per E-Mail“ der bessere Weg zum Zurücksetzen: die Person legt ihr Passwort selbst fest, und niemand sonst sieht es. Ein Einmalpasswort beendet sofort alle Anmeldungen der Person."
          : "Passwort zurücksetzen beendet alle Anmeldungen der Person und erzeugt ein neues Einmalpasswort. Ist der Mailversand eingerichtet, geht das auch per Link, den nur die Person selbst sieht."} Wer IBANs sehen darf
        (Buchhaltung, Administration), meldet sich zusätzlich mit einem Code aus einer
        Authenticator-App an.
      </p>
    </Section>
  );
}

interface NewUserBody {
  username: string;
  displayName: string;
  role: Role;
  email: string | null;
  passwordAccess: boolean;
}

interface LinkRequestDto {
  id: string;
  oidTail: string;
  loginName: string | null;
  displayName: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  attempts: number;
  suggestedUserId: string | null;
}

/**
 * Microsoft-Konten, die sich angemeldet haben und noch keinem Zugang
 * zugeordnet sind. Eine passende E-Mail-Adresse ist nur ein Vorschlag: im
 * Tenant werden Adressen neu vergeben, und wer die Adresse der Vorgängerin
 * bekommt, soll nicht still deren Rolle erben. Deshalb bestätigt hier ein
 * Mensch, mit Name, Anmeldename und Objekt-ID vor Augen.
 */
function LinkRequests({
  requests,
  users,
  onDone,
}: {
  requests: LinkRequestDto[];
  users: UserDto[];
  onDone: () => Promise<unknown>;
}) {
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [arming, setArming] = useState<string | null>(null);
  const confirm = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      api.post(`/users/microsoft-requests/${id}/confirm`, { userId }),
    onSuccess: async () => {
      setArming(null);
      await onDone();
    },
  });
  const reject = useMutation({
    mutationFn: (id: string) => api.post(`/users/microsoft-requests/${id}/reject`),
    onSuccess: async () => {
      setArming(null);
      await onDone();
    },
  });

  return (
    <div className="card mb-6 border-warn/40 p-5">
      <div className="label mb-1">· Wartet auf Zuordnung</div>
      <p className="mb-4 max-w-3xl text-sm text-muted">
        Diese Microsoft-Konten haben sich angemeldet, gehören aber noch zu keinem Zugang.
        Ordnen Sie jedes bewusst zu — erst danach kommt die Person herein. Unbekannt? Ablehnen.
      </p>
      <div className="overflow-x-auto">
        <table className="tbl">
          <thead>
            <tr>
              <th>Microsoft-Konto</th>
              <th>Zuletzt</th>
              <th>Zuordnen zu</th>
              <th className="text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => {
              const userId = choice[r.id] ?? r.suggestedUserId ?? "";
              const target = users.find((u) => u.id === userId);
              return (
                <tr key={r.id}>
                  <td>
                    <div>{r.displayName ?? "ohne Namen"}</div>
                    <div className="font-mono text-xs text-muted">{r.loginName ?? "ohne Anmeldenamen"}</div>
                    <div className="font-mono text-[10px] text-muted" title="Die letzten Stellen der festen Objekt-ID">
                      ID …{r.oidTail}
                    </div>
                  </td>
                  <td className="text-xs text-muted">
                    {dateTime(r.lastSeenAt)}
                    {r.attempts > 1 ? ` · ${r.attempts}×` : ""}
                  </td>
                  <td>
                    <select
                      className="field w-56 py-1"
                      value={userId}
                      onChange={(e) => setChoice((prev) => ({ ...prev, [r.id]: e.target.value }))}
                    >
                      <option value="">— Zugang wählen —</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.displayName} ({u.roleLabel})
                          {u.id === r.suggestedUserId ? " · E-Mail passt" : ""}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="text-right">
                    <div className="flex flex-wrap justify-end gap-1">
                      {arming === `ok-${r.id}` && target ? (
                        <span className="inline-flex gap-1">
                          <button
                            className="btn-primary"
                            onClick={() => confirm.mutate({ id: r.id, userId: target.id })}
                            disabled={confirm.isPending}
                          >
                            Ja, {target.displayName} zuordnen
                          </button>
                          <button className="btn-ghost" onClick={() => setArming(null)}>
                            Nein
                          </button>
                        </span>
                      ) : (
                        <button className="btn-ghost" disabled={!target} onClick={() => setArming(`ok-${r.id}`)}>
                          Zuordnen
                        </button>
                      )}
                      {arming === `no-${r.id}` ? (
                        <span className="inline-flex gap-1">
                          <button className="btn-danger" onClick={() => reject.mutate(r.id)} disabled={reject.isPending}>
                            Ja, ablehnen
                          </button>
                          <button className="btn-ghost" onClick={() => setArming(null)}>
                            Nein
                          </button>
                        </span>
                      ) : (
                        <button className="btn-ghost" onClick={() => setArming(`no-${r.id}`)}>
                          Ablehnen
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {confirm.error ? (
        <div className="mt-3">
          <ErrorBox error={confirm.error} />
        </div>
      ) : null}
      {reject.error ? (
        <div className="mt-3">
          <ErrorBox error={reject.error} />
        </div>
      ) : null}
    </div>
  );
}

function NewUser({
  pending,
  error,
  mailEnabled,
  microsoftEnabled,
  onCancel,
  onSave,
}: {
  pending: boolean;
  error: unknown;
  mailEnabled: boolean;
  microsoftEnabled: boolean;
  onCancel: () => void;
  onSave: (body: NewUserBody) => void;
}) {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [touched, setTouched] = useState(false);
  const [role, setRole] = useState<Role>("Tips.Finance");
  // Mit Microsoft ist das Passwort die Ausnahme: nur für ausdrücklich
  // festgelegte Notfallzugänge, sonst umginge es die Sperre bei Microsoft.
  const [withPassword, setWithPassword] = useState(!microsoftEnabled);

  // Vorschlag aus dem Namen: „Jürgen Weiß" → „juergen.weiss". Überschreibbar.
  const suggestion = displayName
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, ".")
    .replace(/^\.+|\.+$/g, "");
  const effective = touched ? username : suggestion;

  return (
    <div className="card mb-6 p-5">
      <div className="grid gap-4 md:grid-cols-2">
        <label className="block">
          <span className="label mb-1 block">Name</span>
          <input
            className="field"
            autoFocus
            placeholder="Anna Bauer"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="label mb-1 block">Anmeldename</span>
          <input
            className="field font-mono"
            value={effective}
            onChange={(e) => {
              setTouched(true);
              setUsername(e.target.value);
            }}
          />
        </label>
        <label className="block md:col-span-2">
          <span className="label mb-1 block">E-Mail (freiwillig)</span>
          <input
            className="field"
            type="email"
            placeholder="anna.bauer@beispiel.de"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <span className="mt-1 block text-xs text-muted">
            {microsoftEnabled
              ? "Die Microsoft-Anmeldeadresse der Person (meist die Firmen-Mail). Sie dient nur als Vorschlag, wenn das Microsoft-Konto zum ersten Mal auftaucht — verknüpft wird erst, wenn Sie es bestätigen."
              : mailEnabled
                ? "Damit geht „Passwort vergessen“ ohne die Administration."
                : "Wird erst gebraucht, wenn der Mailversand eingerichtet ist."}
          </span>
        </label>
      </div>
      {microsoftEnabled ? (
        <fieldset className="mt-4">
          <legend className="label mb-2">Anmeldung</legend>
          <div className="grid gap-2 md:grid-cols-2">
            {[
              {
                value: false,
                label: "Nur mit Microsoft",
                text: "Empfohlen. Wer bei Microsoft gesperrt wird, kommt auch hier nicht mehr rein.",
              },
              {
                value: true,
                label: "Zusätzlich mit Passwort",
                text: "Nur für einen Notfallzugang, falls Microsoft nicht geht. Mit Einmalpasswort und Code aus der Authenticator-App.",
              },
            ].map((o) => (
              <label
                key={String(o.value)}
                className={`flex cursor-pointer gap-3 rounded border p-3 text-sm ${
                  withPassword === o.value ? "border-accent bg-surface" : "border-line"
                }`}
              >
                <input
                  type="radio"
                  name="anmeldung"
                  checked={withPassword === o.value}
                  onChange={() => setWithPassword(o.value)}
                  className="mt-1"
                />
                <span>
                  <span className="block font-medium">{o.label}</span>
                  <span className="text-xs text-muted">{o.text}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}
      <fieldset className="mt-4">
        <legend className="label mb-2">Rolle</legend>
        <div className="grid gap-2 md:grid-cols-2">
          {ROLLEN.map((r) => (
            <label
              key={r.value}
              className={`flex cursor-pointer gap-3 rounded border p-3 text-sm ${
                role === r.value ? "border-accent bg-surface" : "border-line"
              }`}
            >
              <input
                type="radio"
                name="rolle"
                checked={role === r.value}
                onChange={() => setRole(r.value)}
                className="mt-1"
              />
              <span>
                <span className="block font-medium">{r.label}</span>
                <span className="text-xs text-muted">{r.text}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      {error ? (
        <div className="mt-4">
          <ErrorBox error={error} />
        </div>
      ) : null}
      <div className="mt-4 flex gap-2">
        <button
          className="btn-primary"
          disabled={pending || displayName.trim().length < 2 || effective.length < 2}
          onClick={() =>
            onSave({ username: effective, displayName, role, email: email.trim() || null, passwordAccess: withPassword })
          }
        >
          {pending ? "Legt an …" : withPassword ? "Anlegen und Einmalpasswort erzeugen" : "Anlegen"}
        </button>
        <button className="btn-ghost" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </div>
  );
}
